import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import RemoveIcon from '@mui/icons-material/Remove';
import { alpha, Box, Collapse, Grid, type Theme, Typography } from '@mui/material';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommentsDrawer } from '@/comments';
import { Loading } from '@/components/Loading/Loading';
import { styles } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { useCommentDetail } from '@/data/entities/comments';
import { CommentableStatus } from '@/enums';
import { useRequiredAppSelector, useToken } from '@/hooks';
import { useCommentWebSocket } from '@/hooks/useCommentWebSocket';
import type { IComment } from '@/types';
import { nl2br } from '@/utils/helpers';
import { CommentInputUpdateWithButton } from './CommentInputUpdateWithButton';
import { CommentInputWithButton } from './CommentInputWithButton';
import { CommentItemHeader } from './CommentItemHeader';
import { CommentItemHeaderActionsMore } from './CommentItemHeaderActionsMore';
import { CommentItemHeaderActionsResolve } from './CommentItemHeaderActionsResolve';

const PREVIEW_COMMENTS_NUMBER = 2;

interface Props extends IComment {
    open: boolean;
    scrollToId?: number | null;
    project_id?: number;
}

export const CommentBlock = (props: Props) => {
    const projectId = useRequiredAppSelector('projectId');
    const [, drawerActions] = useCommentsDrawer();
    const { t } = useTranslation();
    const token = useToken();

    const { text, id, user, updated_at, commentable, status, open = false, scrollToId = null, project_id } = props;
    const [scroll_id, setScrollId] = useState(scrollToId);
    const [collapse, setCollapse] = useState(open);
    const [edit, setEdit] = useState<number | null>(null);
    const [showAddInput, setShowAddInput] = useState(true);

    const { data: commentData } = useCommentDetail(
        {
            id,
            projectId: project_id || projectId,
            commentableId: [commentable.id],
            commentableModel: commentable.type,
        },
        { enabled: !!(project_id || projectId) },
    );

    useCommentWebSocket(id, token);

    useEffect(() => {
        setShowAddInput(scrollToId === id);
    }, [scrollToId, id]);

    useLayoutEffect(() => {
        if (commentData && scroll_id) {
            const elem = document.getElementById(String(scroll_id));

            if (elem) {
                elem.scrollIntoView({
                    block: 'center',
                    inline: 'center',
                    behavior: 'smooth',
                });
                setScrollId(null);
            }
        }
    }, [scroll_id, commentData]);

    const highlight = scrollToId === id ? { border: (theme: Theme) => `1px solid ${theme.custom.c39}` } : {};

    const CommentItems = ({ items }: { items: IComment['children'] }) => {
        return (
            <>
                {items.map(item => (
                    <Grid container key={item.id} id={String(item.id)} position="relative" sx={{ padding: '8px 0' }}>
                        <CommentItemHeader user={item.user} updatedAt={item.updated_at}>
                            <CommentItemHeaderActionsMore
                                id={item.id}
                                textAtomId={commentable.id}
                                textContent={commentable.content}
                                handleEdit={() => setEdit(item.id)}
                            />
                        </CommentItemHeader>

                        {edit === item.id ? (
                            <CommentInputUpdateWithButton id={item.id} initialValue={item.text} handleCancel={() => setEdit(null)} />
                        ) : (
                            <Typography
                                variant="body1"
                                color={theme => (theme.palette.mode === 'dark' ? 'primary.main' : 'text.primary')}
                                children={item.text}
                            />
                        )}

                        {item.id === scrollToId ? <Box sx={[styles.notificationIndicator]} /> : null}
                    </Grid>
                ))}
            </>
        );
    };

    return (
        <Grid
            container
            id={id.toString()}
            onClick={() => {
                drawerActions.focus(id, commentable.id, true);
                setScrollId(id);
            }}
            sx={{
                borderRadius: '16px',
                background: theme => (theme.palette.mode === 'dark' ? alpha(theme.custom.c20, 0.08) : theme.palette.background.paper),
                padding: '20px 16px',
                ...highlight,
            }}
        >
            <CommentItemHeader user={user} updatedAt={updated_at}>
                <CommentItemHeaderActionsResolve id={id} textAtomId={commentable.id} status={status} />
                <CommentItemHeaderActionsMore
                    id={id}
                    textAtomId={commentable.id}
                    textContent={commentable.content}
                    handleEdit={() => setEdit(id)}
                />
            </CommentItemHeader>

            <Typography
                variant="body1"
                color={theme => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.text.primary)}
                mt="5px"
            >
                {edit === id ? (
                    <CommentInputUpdateWithButton id={id} initialValue={text} handleCancel={() => setEdit(null)} />
                ) : (
                    nl2br(text)
                )}
            </Typography>
            <Box
                sx={{
                    paddingLeft: '12px',
                    marginLeft: '12px',
                    marginTop: '8px',
                    borderLeft: theme => `1px solid ${theme.custom.c47}`,
                    width: '100%',
                }}
            >
                {commentData?.length === 1 ? (
                    <CommentItems items={commentData[0]?.children.slice(0, PREVIEW_COMMENTS_NUMBER) ?? []} />
                ) : null}

                <Collapse in={collapse} sx={{ width: '100%' }}>
                    {commentData?.length === 1 ? (
                        <CommentItems items={commentData[0]?.children.slice(PREVIEW_COMMENTS_NUMBER) ?? []} />
                    ) : (
                        <div className="mt-[30px]">
                            <Loading />
                        </div>
                    )}
                </Collapse>
                {status === CommentableStatus.active && showAddInput && edit === null ? (
                    <CommentInputWithButton
                        textAtomContent={''}
                        queryArgs={{ parentId: id, project_id: project_id || projectId }}
                        handleCancel={() => setShowAddInput(false)}
                    />
                ) : null}
                {commentData?.length === 1 && (commentData[0]?.children.length ?? 0) > PREVIEW_COMMENTS_NUMBER ? (
                    <Button variant="text" onClick={() => setCollapse(prev => !prev)} className="p-0 [&_svg]:text-primary">
                        {collapse ? (
                            <>
                                <RemoveIcon
                                    sx={{
                                        fontSize: '1rem',
                                        marginRight: '6px',
                                        color: theme => `${theme.palette.error.main}!important`,
                                    }}
                                />
                                {t('comment.collapse')}
                                <ChatIcon sx={{ fontSize: '1rem', marginLeft: '10px' }} />
                            </>
                        ) : (
                            <>
                                <AddIcon
                                    sx={{
                                        fontSize: '1rem',
                                        marginRight: '6px',
                                        color: theme => `${theme.palette.error.main}!important`,
                                    }}
                                />
                                {t('comment.replies', {
                                    count: (commentData[0]?.children.length ?? 0) - PREVIEW_COMMENTS_NUMBER,
                                })}
                                <ChatIcon sx={{ fontSize: '1rem', marginLeft: '10px' }} />
                            </>
                        )}
                    </Button>
                ) : null}
            </Box>
        </Grid>
    );
};
